import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Plus, Upload, Trash2, Eye, CheckCircle, X,
  Image as ImageIcon, Calendar, Power, PowerOff, Info
} from 'lucide-react';
import AdminLayout from '../../components/admin/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { adminApi } from '../../lib/api';
import { resolveAssetUrl } from '../../config';
import { toast } from 'sonner';

const CoasterDesigns = () => {
  const [designs, setDesigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [admin, setAdmin] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    season: 'winter',
    year: new Date().getFullYear()
  });
  const [designFile, setDesignFile] = useState(null);
  const [designPreview, setDesignPreview] = useState(null);

  useEffect(() => {
    fetchDesigns();
    fetchAdminProfile();
  }, []);

  const fetchAdminProfile = async () => {
    try {
      const response = await adminApi.get(`/admin/me`);
      setAdmin(response.data);
    } catch (error) {
      console.error('Failed to fetch admin profile:', error);
    }
  };

  const fetchDesigns = async () => {
    try {
      setLoading(true);
      const response = await adminApi.get(`/admin/coaster-designs`);
      setDesigns(response.data);
    } catch (error) {
      console.error('Failed to fetch designs:', error);
      toast.error('Failed to load coaster designs');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File size must be less than 10MB');
        return;
      }
      setDesignFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setDesignPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUpload = async () => {
    if (!designFile || !formData.name) {
      toast.error('Please fill in all fields and select a design');
      return;
    }

    setUploading(true);
    try {
      // Step 1: Upload the image file
      const uploadFormData = new FormData();
      uploadFormData.append('design', designFile);

      const uploadResponse = await adminApi.post(
        `/admin/coaster-designs/upload`,
        uploadFormData
      );

      const imageUrl = uploadResponse.data.image_url;

      // Step 2: Create the design record
      await adminApi.post(
        `/admin/coaster-designs`,
        {
          name: formData.name,
          season: formData.season,
          year: formData.year,
          image_url: imageUrl,
          created_by_admin_id: admin?.id
        }
      );

      toast.success('Coaster design uploaded successfully!');
      setShowUploadModal(false);
      resetForm();
      fetchDesigns();
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload design');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (designId) => {
    if (!confirm('Are you sure you want to delete this design?')) return;

    try {
      await adminApi.delete(`/admin/coaster-designs/${designId}`);
      toast.success('Design deleted successfully');
      fetchDesigns();
    } catch (error) {
      console.error('Delete failed:', error);
      toast.error('Failed to delete design');
    }
  };

  const handleToggleStatus = async (designId, currentStatus) => {
    const action = currentStatus ? 'deactivate' : 'activate';
    
    try {
      const response = await adminApi.patch(
        `/admin/coaster-designs/${designId}/toggle-status`,
        {}
      );
      
      toast.success(response.data.message);
      fetchDesigns();
    } catch (error) {
      console.error(`Failed to ${action}:`, error);
      const errorMsg = error.response?.data?.detail || `Failed to ${action} design`;
      toast.error(errorMsg);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      season: 'winter',
      year: new Date().getFullYear()
    });
    setDesignFile(null);
    setDesignPreview(null);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Filter designs based on status
  const filteredDesigns = designs.filter(design => {
    if (filterStatus === 'all') return true;
    if (filterStatus === 'active') return design.is_active === true;
    if (filterStatus === 'inactive') return design.is_active === false;
    return true;
  });

  const activeCount = designs.filter(d => d.is_active).length;
  const inactiveCount = designs.filter(d => !d.is_active).length;

  // Loading state
  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-business border-t-transparent"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      {/* V5 Page Header */}
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-8">
        <div>
          <p className="font-mono text-venue text-sm mb-1">// coaster_designs</p>
          <h1 className="font-display text-3xl text-navy">Coaster Designs</h1>
          <p className="text-navy/50 mt-1">
            Manage seasonal coaster designs for venue partners • {designs.length} total
          </p>
        </div>
        <Button
          onClick={() => setShowUploadModal(true)}
          className="flex items-center gap-2 self-start"
          style={{
            background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
            color: 'white'
          }}
        >
          <Plus size={18} />
          Upload Design
        </Button>
      </div>

      {/* V5 Filter Tabs */}
      <div className="flex flex-wrap gap-2 mb-6">
        <button
          onClick={() => setFilterStatus('all')}
          className={`px-4 py-2 rounded-full font-medium text-sm transition-all ${
            filterStatus === 'all'
              ? 'bg-business text-white shadow-lg'
              : 'bg-white text-navy/70 hover:bg-navy/5 border border-navy/10'
          }`}
          style={filterStatus === 'all' ? {
            boxShadow: '0 4px 15px rgba(139, 92, 246, 0.4)'
          } : {}}
        >
          All ({designs.length})
        </button>
        <button
          onClick={() => setFilterStatus('active')}
          className={`px-4 py-2 rounded-full font-medium text-sm transition-all ${
            filterStatus === 'active'
              ? 'bg-green-500 text-white shadow-lg'
              : 'bg-white text-navy/70 hover:bg-navy/5 border border-navy/10'
          }`}
          style={filterStatus === 'active' ? {
            boxShadow: '0 4px 15px rgba(34, 197, 94, 0.4)'
          } : {}}
        >
          Active ({activeCount})
        </button>
        <button
          onClick={() => setFilterStatus('inactive')}
          className={`px-4 py-2 rounded-full font-medium text-sm transition-all ${
            filterStatus === 'inactive'
              ? 'bg-navy/60 text-white'
              : 'bg-white text-navy/70 hover:bg-navy/5 border border-navy/10'
          }`}
        >
          Inactive ({inactiveCount})
        </button>
      </div>

      {/* V5 Design Requirements Info Card */}
      <div className="bg-white rounded-2xl p-5 mb-6 border border-navy/5 shadow-sm" style={{ borderLeft: '4px solid #14b8a6' }}>
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-venue/10 rounded-xl flex items-center justify-center flex-shrink-0">
            <Info className="w-5 h-5 text-venue" />
          </div>
          <div>
            <h3 className="font-semibold text-navy mb-2">Design Requirements</h3>
            <ul className="text-sm text-navy/70 space-y-1">
              <li>• <strong>Top area</strong>: Reserved for venue logo placement</li>
              <li>• <strong>Bottom right (~20%)</strong>: Reserved for unique QR code</li>
              <li>• Recommended size: 1200×1200px or higher</li>
              <li>• Format: PNG with transparent areas for logo/QR overlay</li>
            </ul>
            <p className="text-xs text-navy/50 mt-3 bg-cream p-2 rounded-lg">
              💡 Newly uploaded designs will be <strong>inactive</strong> by default. You can activate up to 3 designs at a time.
            </p>
          </div>
        </div>
      </div>

      {/* Empty State */}
      {filteredDesigns.length === 0 ? (
        <div className="bg-white rounded-2xl p-12 text-center shadow-sm border border-navy/5">
          <div className="w-20 h-20 bg-navy/5 rounded-full flex items-center justify-center mx-auto mb-6">
            <ImageIcon className="w-10 h-10 text-navy/20" />
          </div>
          <h3 className="font-display text-xl text-navy mb-2">
            {filterStatus === 'all' 
              ? 'No Coaster Designs Yet' 
              : `No ${filterStatus.charAt(0).toUpperCase() + filterStatus.slice(1)} Designs`
            }
          </h3>
          <p className="text-navy/50 mb-6">
            {filterStatus === 'all'
              ? 'Upload your first seasonal coaster design to get started'
              : `No designs are currently marked as ${filterStatus}`
            }
          </p>
          {filterStatus === 'all' && (
            <Button
              onClick={() => setShowUploadModal(true)}
              style={{
                background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                color: 'white'
              }}
            >
              <Plus size={18} className="mr-2" />
              Upload Design
            </Button>
          )}
        </div>
      ) : (
        /* V5 Designs Grid */
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredDesigns.map((design) => (
            <motion.div
              key={design.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-2xl shadow-sm hover:shadow-md transition-all border border-navy/5 overflow-hidden group"
            >
              {/* Design Preview */}
              <div className="aspect-square bg-cream flex items-center justify-center p-4 relative">
                <img
                  src={resolveAssetUrl(design.image_url)}
                  alt={design.name}
                  className="max-w-full max-h-full object-contain rounded-lg"
                />
                {/* Status Badge Overlay */}
                <div className="absolute top-3 right-3">
                  {design.is_active ? (
                    <span className="flex items-center gap-1 text-xs font-semibold text-green-600 bg-green-50 px-2.5 py-1 rounded-full border border-green-200">
                      <CheckCircle size={12} />
                      Active
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 text-xs font-semibold text-navy/50 bg-navy/5 px-2.5 py-1 rounded-full border border-navy/10">
                      Inactive
                    </span>
                  )}
                </div>
              </div>

              {/* Design Info */}
              <div className="p-4">
                <div className="mb-3">
                  <h3 className="font-semibold text-navy text-lg">{design.name}</h3>
                  <p className="text-sm text-navy/50 capitalize">{design.season} {design.year}</p>
                </div>

                <p className="text-xs text-navy/40 mb-4 flex items-center gap-1">
                  <Calendar size={12} />
                  Added {formatDate(design.created_at)}
                </p>

                {/* Actions */}
                <div className="flex gap-2 mb-3">
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex-1 border-navy/10 text-navy/70 hover:bg-navy/5"
                    onClick={() => window.open(resolveAssetUrl(design.image_url), '_blank')}
                  >
                    <Eye size={14} className="mr-1" />
                    View
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-red-200 text-red-500 hover:bg-red-50"
                    onClick={() => handleDelete(design.id)}
                  >
                    <Trash2 size={14} />
                  </Button>
                </div>

                {/* Toggle Active Status Button */}
                <Button
                  size="sm"
                  className="w-full"
                  style={!design.is_active ? {
                    background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                    color: 'white'
                  } : {
                    background: 'transparent',
                    border: '2px solid rgba(30, 27, 75, 0.1)',
                    color: 'rgba(30, 27, 75, 0.7)'
                  }}
                  onClick={() => handleToggleStatus(design.id, design.is_active)}
                >
                  {design.is_active ? (
                    <>
                      <PowerOff size={14} className="mr-1" />
                      Deactivate
                    </>
                  ) : (
                    <>
                      <Power size={14} className="mr-1" />
                      Activate
                    </>
                  )}
                </Button>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* V5 Upload Modal */}
      {showUploadModal && (
        <>
          <div
            className="fixed inset-0 bg-navy/50 z-50"
            onClick={() => setShowUploadModal(false)}
          />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-2xl shadow-2xl max-w-xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="border-b border-navy/5 px-6 py-4 flex items-center justify-between sticky top-0 bg-white z-10">
                <div>
                  <p className="font-mono text-venue text-xs mb-0.5">// upload_design</p>
                  <h2 className="font-display text-xl text-navy">
                    Upload Coaster Design
                  </h2>
                </div>
                <button
                  onClick={() => setShowUploadModal(false)}
                  className="p-2 hover:bg-navy/5 rounded-xl transition-colors"
                >
                  <X size={20} className="text-navy/50" />
                </button>
              </div>

              {/* Modal Content */}
              <div className="px-6 py-6">
                <div className="space-y-5">
                  {/* Design Name */}
                  <div>
                    <label className="block text-sm font-medium text-navy/70 mb-2">
                      Design Name <span className="text-red-500">*</span>
                    </label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="e.g., Winter Wonderland 2025"
                      className="h-12 border-2 border-navy/10 focus:border-business rounded-xl"
                    />
                  </div>

                  {/* Season & Year */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-navy/70 mb-2">
                        Season
                      </label>
                      <select
                        value={formData.season}
                        onChange={(e) => setFormData({ ...formData, season: e.target.value })}
                        className="w-full h-12 px-4 border-2 border-navy/10 rounded-xl focus:outline-none focus:border-business bg-white text-navy"
                      >
                        <option value="winter">Winter</option>
                        <option value="spring">Spring</option>
                        <option value="summer">Summer</option>
                        <option value="fall">Fall</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-navy/70 mb-2">
                        Year
                      </label>
                      <Input
                        type="number"
                        value={formData.year}
                        onChange={(e) => setFormData({ ...formData, year: parseInt(e.target.value) })}
                        min="2020"
                        max="2030"
                        className="h-12 border-2 border-navy/10 focus:border-business rounded-xl"
                      />
                    </div>
                  </div>

                  {/* File Upload */}
                  <div>
                    <label className="block text-sm font-medium text-navy/70 mb-2">
                      Design File <span className="text-red-500">*</span>
                    </label>
                    {designPreview ? (
                      <div className="relative">
                        <div className="border-2 border-navy/10 rounded-xl p-4 bg-cream">
                          <img
                            src={designPreview}
                            alt="Preview"
                            className="max-h-48 mx-auto rounded-lg"
                          />
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          className="mt-3 border-navy/10"
                          onClick={() => {
                            setDesignFile(null);
                            setDesignPreview(null);
                          }}
                        >
                          Change File
                        </Button>
                      </div>
                    ) : (
                      <label className="border-2 border-dashed border-navy/20 rounded-xl p-8 text-center cursor-pointer hover:border-business hover:bg-business/5 transition-all block">
                        <input
                          type="file"
                          accept="image/png,image/jpeg"
                          onChange={handleFileSelect}
                          className="hidden"
                        />
                        <div className="w-12 h-12 bg-business/10 rounded-xl flex items-center justify-center mx-auto mb-3">
                          <Upload size={24} className="text-business" />
                        </div>
                        <p className="text-sm font-medium text-navy">
                          Click to upload design
                        </p>
                        <p className="text-xs text-navy/50 mt-1">
                          PNG or JPG (max 10MB)
                        </p>
                      </label>
                    )}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 mt-6">
                  <Button
                    variant="outline"
                    className="flex-1 border-navy/10 text-navy/70"
                    onClick={() => setShowUploadModal(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleUpload}
                    disabled={uploading}
                    className="flex-1"
                    style={{
                      background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                      color: 'white'
                    }}
                  >
                    {uploading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="mr-2" size={16} />
                        Upload Design
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AdminLayout>
  );
};

export default CoasterDesigns;
